'use client';

import { useEffect, useState } from 'react';

import * as tracker from '@/infra/tracking/events';
import { useSellers } from '@/presenters/hooks/catalog';
import { useProductStore } from '@/presenters/store/catalog/product';

import { getDataLayerItem } from '../../utils';

type GTMProductPageViewProps = {
  itemId: number;
};

export default function GTMProductPageView(
  props: Readonly<GTMProductPageViewProps>
) {
  const { itemId } = props;

  const { product, category, currentSeller, variants } = useProductStore(
    (store) => store
  );

  const [shouldTriggerDataLayer, setShouldTriggerDataLayer] = useState(false);
  const [viewItem, setViewItem] = useState<tracker.PushViewItemProps>();

  const {
    data: sellers,
    isFetching: isSellersFetching,
    isFetched: isSellersFetched,
  } = useSellers(itemId);

  useEffect(() => {
    if (isSellersFetching || !isSellersFetched) {
      return;
    }

    setViewItem((prev) => {
      const isBox = product.pack && product.pack.packaging !== 1;
      const canPurchase = currentSeller.ecommerceScopes.canPurchase;
      const canPickupInStore = currentSeller.ecommerceScopes.canPickupInStore;
      const hasMultiSeller = (sellers && sellers.length > 1) || false;

      const currentData: tracker.PushViewItemProps = {
        item: getDataLayerItem({
          product,
          currentSeller,
          category,
          variants,
          quantity: isBox ? product.pack.packaging : 1,
          boxQuantity: 1,
        }),
        hasBuyBox: hasMultiSeller ?? false,
        isAvailable: (canPurchase || canPickupInStore) ?? undefined,
      };

      setShouldTriggerDataLayer(currentData !== prev);

      return currentData;
    });
  }, [
    product,
    category,
    currentSeller,
    variants,
    sellers,
    isSellersFetching,
    isSellersFetched,
  ]);

  useEffect(() => {
    if (shouldTriggerDataLayer && viewItem) {
      tracker.pushViewItem(viewItem);
    }
  }, [viewItem, shouldTriggerDataLayer]);

  return null;
}
